import Vue, { getCurrentInstance } from 'vue';
import { useNuxt2App } from '~/utils/nuxt-instance';

type RoutingHelper =
  | 'localePath'
  | 'localeRoute'
  | 'localeLocation'
  | 'switchLocalePath';

/**
 * nuxt-i18n routing helpers.
 *
 * These are added through a mixin's `methods` (see
 * .nuxt/nuxt-i18n/plugin.routing.js), and Vue 2.7 runs initMethods *after*
 * setup() — so `vm.localePath` does not exist yet while a composable runs.
 * Resolve it at call time instead of binding it up front.
 *
 * The instance version closes over the live `this.$route` while the context one
 * closes over `context.route`; they disagree for relative or named routes, so
 * the instance version wins whenever there is one. Both need to be invoked with
 * the vm as `this` (VueInstanceProxy reads off it).
 */
function lazyRoutingHelper<K extends RoutingHelper>(key: K): Vue[K] {
  const vm = getCurrentInstance()?.proxy;
  // Outside a component there is no instance method to wait for, and the
  // context helper is self-contained, so resolve it immediately
  const contextHelper = vm ? null : (useNuxt2App() as any)[key];

  return function (...args: any[]) {
    const helper = (vm as any)?.[key] ?? contextHelper;
    return helper.apply(vm, args);
  } as Vue[K];
}

export function useLocalePath(): Vue['localePath'] {
  return lazyRoutingHelper('localePath');
}

export function useLocaleRoute(): Vue['localeRoute'] {
  return lazyRoutingHelper('localeRoute');
}

export function useSwitchLocalePath(): Vue['switchLocalePath'] {
  return lazyRoutingHelper('switchLocalePath');
}
